import { motion } from "framer-motion";

/* ---------------------------------------------------------------------
   Floating "glass panel" widget — the small live-data cards that sit on
   top of the hero photo (inspired by the layered HUD-style overlays on
   dashboard-style hero sections, built from scratch for Eagle Eye).
--------------------------------------------------------------------- */
export function HeroWidget({ icon: Icon, label, value, sub, className = "", delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 14, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ delay, duration: 0.6, ease: "easeOut" }}
      className={`absolute rounded-2xl border border-white/15 bg-white/[0.07] backdrop-blur-xl px-4 py-3 shadow-2xl ${className}`}
    >
      <div className="flex items-center gap-1.5 text-white/80 text-[11px] font-medium mb-1 uppercase tracking-wide">
        <Icon className="h-3.5 w-3.5 text-eagle-gold" strokeWidth={2} /> {label}
      </div>
      <div className="text-white font-display font-bold text-lg leading-none whitespace-nowrap">{value}</div>
      {sub && <div className="text-[11px] text-white/45 mt-1 whitespace-nowrap">{sub}</div>}
    </motion.div>
  );
}

/* ---------------------------------------------------------------------
   Shared radar-style backdrop rings, used behind every hero illustration
   so the three slides feel like one family of graphics.
--------------------------------------------------------------------- */
function RadarRings({ cx = 210, cy = 210 }) {
  return (
    <>
      <circle cx={cx} cy={cy} r="190" fill="none" stroke="white" strokeOpacity="0.06" />
      <circle cx={cx} cy={cy} r="150" fill="none" stroke="#FFB703" strokeOpacity="0.12" strokeDasharray="2 8" />
      <circle cx={cx} cy={cy} r="110" fill="none" stroke="white" strokeOpacity="0.08" />
    </>
  );
}

/* ---------------------------------------------------------------------
   CCTV camera illustration — bullet camera on a wall bracket, with a
   pulsing "recording" dot and radiating scan lines from the lens.
--------------------------------------------------------------------- */
export function CctvIllustration({ className = "" }) {
  return (
    <svg viewBox="0 0 420 420" className={className} fill="none" xmlns="http://www.w3.org/2000/svg">
      <RadarRings cx={210} cy={210} />
      {/* scan arcs from the lens */}
      <g stroke="#FFB703" strokeOpacity="0.35" strokeWidth="1.5" strokeLinecap="round">
        <path d="M 250 175 A 60 60 0 0 1 280 210" />
        <path d="M 258 155 A 90 90 0 0 1 305 210" />
      </g>
      {/* wall mount bracket */}
      <rect x="330" y="150" width="18" height="70" rx="4" fill="white" fillOpacity="0.08" stroke="white" strokeOpacity="0.25" />
      <path d="M 262 185 L 330 168" stroke="white" strokeOpacity="0.3" strokeWidth="6" strokeLinecap="round" />
      {/* camera body */}
      <rect x="120" y="160" width="145" height="70" rx="30" fill="#0A0A0A" stroke="white" strokeOpacity="0.25" strokeWidth="1.5" />
      <rect x="120" y="160" width="145" height="70" rx="30" fill="white" fillOpacity="0.04" />
      {/* lens housing */}
      <circle cx="150" cy="195" r="34" fill="#111" stroke="#FFB703" strokeOpacity="0.6" strokeWidth="2" />
      <circle cx="150" cy="195" r="22" fill="#050505" stroke="white" strokeOpacity="0.15" />
      <circle cx="158" cy="187" r="6" fill="white" fillOpacity="0.35" />
      {/* status LED, pulsing */}
      <motion.circle
        cx="245" cy="178" r="5" fill="#FFB703"
        animate={{ opacity: [1, 0.25, 1] }}
        transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
      />
      <text x="222" y="182" fontSize="9" fill="white" fillOpacity="0.5" fontFamily="monospace" letterSpacing="1">REC</text>
    </svg>
  );
}

/* ---------------------------------------------------------------------
   Intercom illustration — a video door-entry panel: camera eye, name
   plate lines, speaker grille and a glowing call button with ripples.
--------------------------------------------------------------------- */
export function IntercomIllustration({ className = "" }) {
  return (
    <svg viewBox="0 0 420 420" className={className} fill="none" xmlns="http://www.w3.org/2000/svg">
      <RadarRings cx={210} cy={210} />
      {/* panel body */}
      <rect x="150" y="90" width="120" height="240" rx="22" fill="#0A0A0A" stroke="white" strokeOpacity="0.25" strokeWidth="1.5" />
      <rect x="150" y="90" width="120" height="240" rx="22" fill="white" fillOpacity="0.04" />
      {/* camera eye */}
      <circle cx="210" cy="128" r="14" fill="#050505" stroke="#FFB703" strokeOpacity="0.6" strokeWidth="2" />
      <circle cx="210" cy="128" r="6" fill="white" fillOpacity="0.3" />
      {/* speaker grille */}
      {[0, 1, 2, 3].map(i => (
        <rect key={i} x="188" y={160 + i * 9} width="44" height="4" rx="2" fill="white" fillOpacity="0.15" />
      ))}
      {/* name plate lines */}
      <rect x="188" y="212" width="44" height="6" rx="3" fill="white" fillOpacity="0.12" />
      <rect x="188" y="224" width="30" height="6" rx="3" fill="white" fillOpacity="0.12" />
      {/* call button with ripple rings */}
      <motion.circle
        cx="210" cy="270" r="26" fill="none" stroke="#FFB703" strokeOpacity="0.35"
        animate={{ r: [22, 34], opacity: [0.5, 0] }}
        transition={{ duration: 1.8, repeat: Infinity, ease: "easeOut" }}
      />
      <circle cx="210" cy="270" r="20" fill="#FFB703" fillOpacity="0.15" stroke="#FFB703" strokeWidth="2" />
      <circle cx="210" cy="270" r="8" fill="#FFB703" />
    </svg>
  );
}

/* ---------------------------------------------------------------------
   Alarm system illustration — a shield with a keypad grid inside it and
   a status LED strip along the top, echoing an armed control panel.
--------------------------------------------------------------------- */
export function AlarmIllustration({ className = "" }) {
  return (
    <svg viewBox="0 0 420 420" className={className} fill="none" xmlns="http://www.w3.org/2000/svg">
      <RadarRings cx={210} cy={210} />
      {/* shield outline */}
      <path
        d="M210 95 L282 122 V205 C282 258 252 292 210 315 C168 292 138 258 138 205 V122 Z"
        fill="#0A0A0A" fillOpacity="0.9" stroke="#FFB703" strokeOpacity="0.5" strokeWidth="2.5"
      />
      <path
        d="M210 95 L282 122 V205 C282 258 252 292 210 315 C168 292 138 258 138 205 V122 Z"
        fill="white" fillOpacity="0.03"
      />
      {/* keypad grid inside shield */}
      {[0, 1, 2].map(row => (
        [0, 1, 2].map(col => (
          <rect
            key={`${row}-${col}`}
            x={175 + col * 24}
            y={155 + row * 24}
            width="16" height="16" rx="4"
            fill="white" fillOpacity="0.06" stroke="white" strokeOpacity="0.15"
          />
        ))
      ))}
      {/* armed checkmark, pulsing subtly */}
      <motion.path
        d="M186 245 L204 262 L236 226"
        stroke="#FFB703" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" fill="none"
        animate={{ opacity: [1, 0.6, 1] }}
        transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
      />
    </svg>
  );
}